/**
 * Recently Viewed Products
 * - Tracks product handles in localStorage
 * - Fetches product JSON from Shopify
 * - Renders product cards identical to product-recommendations section
 * - Initializes Swiper slider
 */

const STORAGE_KEY = 'recently_viewed_products';
const MAX_STORE = 20;

// ─── Track current product ───────────────────────────────────────────────────
function trackCurrentProduct(handle) {
  if (!handle) return;
  let viewed = getViewed();
  viewed = viewed.filter((h) => h !== handle);
  viewed.unshift(handle);
  if (viewed.length > MAX_STORE) viewed = viewed.slice(0, MAX_STORE);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(viewed));
}

function getViewed() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch (e) {
    return [];
  }
}

// ─── Fetch product JSON ───────────────────────────────────────────────────────
async function fetchProduct(handle) {
  try {
    const res = await fetch(`${window.Shopify.routes.root}products/${handle}.js`);
    if (!res.ok) return null;
    return res.json();
  } catch (e) {
    return null;
  }
}

// ─── Build product card HTML (mirrors product-card.liquid output) ─────────────
function buildCard(product, options = {}) {
  const {
    mediaSize = 'square',
    showVendor = false,
    showDesc = false,
    showSku = false,
    showSecondaryImage = false,
  } = options;

  const variant = product.variants?.[0];
  const available = variant?.available ?? product.available;
  const price = variant?.price ?? product.price;
  const comparePrice = variant?.compare_at_price ?? product.compare_at_price;
  const sku = variant?.sku || '';

  const featuredImage = product.featured_image || (product.images?.[0]);
  const secondImage = product.images?.[1];

  const paddingMap = {
    landscape: '70%',
    portrait: '130%',
    square: '100%',
  };
  const paddingBottom = paddingMap[mediaSize] || '100%';

  // Format money
  const formatMoney = (cents) => {
    const amount = (cents / 100).toFixed(2);
    return `${window.Shopify.currency.active} ${amount}`;
  };

  // Custom compare price (always 15% higher, matching price.liquid logic)
  const customComparePrice = Math.round(price * 1.15);

  // Sale badge
  const onSale = comparePrice && comparePrice > price && available;
  const soldOut = !available;

  const badgeHTML = soldOut
    ? `<span class="badge badge--soldout" aria-hidden="true">Sold out</span>`
    : onSale
    ? `<span class="badge badge--sale" aria-hidden="true">Sale</span>`
    : '';

  // Image HTML
  const sizes = '(min-width: 1100px) 535px, (min-width: 750px) calc((100vw - 40px) / 2), calc(100vw - 20px)';

  const primaryImgHTML = featuredImage
    ? `<img
        src="${featuredImage}"
        alt="${product.title}"
        loading="lazy"
        class="motion-reduce media--first"
        sizes="${sizes}"
        width="533"
        height="533"
        style="object-fit: cover; width: 100%; height: 100%; position: absolute; top: 0; left: 0;"
      >`
    : `<svg viewBox="0 0 525 525" xmlns="http://www.w3.org/2000/svg" class="placeholder-svg" style="width:100%;height:100%;position:absolute;top:0;left:0;"><rect width="525" height="525" fill="#f4f4f4"/></svg>`;

  const secondaryImgHTML =
    showSecondaryImage && secondImage
      ? `<img
          src="${secondImage}"
          alt="${product.title}"
          loading="lazy"
          class="motion-reduce media--second"
          sizes="${sizes}"
          width="533"
          height="533"
          style="object-fit: cover; width: 100%; height: 100%; position: absolute; top: 0; left: 0;"
        >`
      : '';

 
  const customComparePriceHTML = `<s class="price-item price-item--custom-compare" style="color:#888!important;text-decoration:line-through;margin-left:8px;font-weight:400;font-size:0.95em">${formatMoney(customComparePrice)}</s>`;

  const priceHTML = onSale
    ? `<div class="price price--on-sale">
        <dl>
          <div class="price__regular">
            <dd>
              <span class="price-item price-item--regular">${formatMoney(price)}</span>
              ${customComparePriceHTML}
            </dd>
          </div>
          <div class="price__sale">
            <dd>
              <span class="price-item price-item--sale">${formatMoney(price)}</span>
              ${customComparePriceHTML}
            </dd>
            <dd class="price__compare">
              <s class="price-item price-item--regular">${formatMoney(comparePrice)}</s>
            </dd>
          </div>
        </dl>
      </div>`
    : `<div class="price">
        <dl>
          <div class="price__regular">
            <dd>
              <span class="price-item price-item--regular">${formatMoney(price)}</span>
              ${customComparePriceHTML}
            </dd>
          </div>
        </dl>
      </div>`;

  const vendorHTML = showVendor && product.vendor
    ? `<div class="caption-with-letter-spacing subtitle card__vendor">${product.vendor}</div>`
    : '';

  const descHTML = showDesc && product.description
    ? `<div class="card__description">${product.description.replace(/<[^>]*>/g, '').substring(0, 120)}</div>`
    : '';

  const skuHTML = showSku && sku
    ? `<p class="card__sku no-js-hidden"><span class="visually-hidden">SKU:</span>${sku}</p>`
    : '';

  return `
    <div class="card-wrapper js-color-swatches-wrapper" data-product="${product.handle}">
      <span class="visually-hidden">${product.title}</span>
      <div class="card card--product" tabindex="-1">
        <div class="card__inner full-unstyled-link">
          <div
            class="media media--transparent media--${mediaSize}"
            style="padding-bottom: ${paddingBottom}; position: relative; overflow: hidden;"
          >
            ${primaryImgHTML}
            ${secondaryImgHTML}
          </div>

          ${badgeHTML ? `<div class="card__badge">${badgeHTML}</div>` : ''}
        </div>
      </div>

      <div class="card-information">
        <div class="card-information__wrapper">
          ${skuHTML}
          ${vendorHTML}
          <h3 class="card__title h5">
            <a
              class="full-unstyled-link"
              href="/products/${product.handle}"
              title="${product.title}"
            >
              ${product.title}
            </a>
          </h3>
          ${descHTML}
          ${priceHTML}
        </div>
      </div>

      <a
        href="/products/${product.handle}"
        class="link link--overlay card-wrapper__link--overlay js-color-swatches-link"
        aria-label="View ${product.title}"
      ></a>
    </div>
  `;
}

// ─── Custom Element ───────────────────────────────────────────────────────────
class RecentlyViewedProducts extends HTMLElement {
  connectedCallback() {
    const currentProduct = this.dataset.currentProduct;

    // Track BEFORE filtering so current page isn't shown
    trackCurrentProduct(currentProduct);

    const limit = parseInt(this.dataset.productsLimit) || 10;
    const sectionId = this.dataset.sectionId;
    const showVendor = this.dataset.showVendor === 'true';
    const showDesc = this.dataset.showDesc === 'true';
    const showSku = this.dataset.showSku === 'true';
    const mediaSize = this.dataset.imageRatio || 'square';
    const showSecondaryImage = this.dataset.showSecondaryImage === 'true';

    let handles = getViewed();
    // Exclude current product from display
    if (currentProduct) {
      handles = handles.filter((h) => h !== currentProduct);
    }
    handles = handles.slice(0, limit);

    if (handles.length === 0) return; // Nothing to show

    const listEl = this.querySelector(`#recently-viewed-list-${sectionId}`);
    if (!listEl) return;

    listEl.innerHTML = '';

    const cardOptions = { mediaSize, showVendor, showDesc, showSku, showSecondaryImage };

    // Fetch all products in parallel then render in original order
    Promise.all(handles.map((h) => fetchProduct(h))).then((products) => {
      const validProducts = products.filter(Boolean);
      if (validProducts.length === 0) return;

      validProducts.forEach((product) => {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide collection-product-card recently-viewed__slide quickview';
        slide.innerHTML = buildCard(product, cardOptions);
        listEl.appendChild(slide);
      });

      // Show the section
      this.style.display = '';

      // Init Swiper
      this._initSwiper();
    });
  }

  _initSwiper() {
    if (typeof Swiper === 'undefined') {
      console.warn('Recently Viewed: Swiper not found. Make sure Swiper is loaded in theme.liquid.');
      return;
    }

    const slider = this.querySelector('.recently-viewed__slider');
    const btnPrev = this.querySelector('.recently-viewed__btn--prev');
    const btnNext = this.querySelector('.recently-viewed__btn--next');

    if (!slider) return;

    new Swiper(slider, {
      slidesPerView: 1.2,
      spaceBetween: 16,
      grabCursor: true,
      navigation: {
        prevEl: btnPrev,
        nextEl: btnNext,
      },
      breakpoints: {
        480: {
          slidesPerView: 2.2,
          spaceBetween: 16,
        },
        750: {
          slidesPerView: 3,
          spaceBetween: 20,
        },
        1100: {
          slidesPerView: 6,
          spaceBetween: 10,
        },
      },
    });
  }
}

customElements.define('recently-viewed-products', RecentlyViewedProducts);
